import { services } from '@config/services';
import { catalogApi } from './base';
import type {
  Catalog,
  CatalogListResponse,
  CreateCatalogInput,
  PatchCatalogInput,
} from './types';

/**
 * Catalog CRUD endpoints. The list provides per-id + LIST tags; mutations
 * invalidate the list and (for patch) the affected catalog's variant list.
 */
const catalogsApi = catalogApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /catalog/v1/merchantCatalog/list?merchantId= */
    getCatalogs: builder.query<CatalogListResponse, { merchantId: string }>({
      query: ({ merchantId }) => ({
        url: `${services.catalog}/v1/merchantCatalog/list?merchantId=${encodeURIComponent(merchantId)}`,
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: 'CatalogList' as const, id: 'LIST' },
        ...(result?.catalogs ?? []).map((c) => ({ type: 'Catalog' as const, id: c.id })),
      ],
    }),

    /** POST /catalog/v1/merchantCatalog */
    createCatalog: builder.mutation<Catalog, CreateCatalogInput>({
      query: (body) => ({
        url: `${services.catalog}/v1/merchantCatalog`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'CatalogList', id: 'LIST' }],
    }),

    /** PATCH /catalog/v1/merchantCatalog — catalog metadata only (enable/disable,
     *  rename, sub-account reassignment). Variant writes go through the dedicated
     *  `/v1/merchantVariants` endpoints. Also busts the catalog's variant list,
     *  because that response embeds the catalog (name, owner, isEnabled). */
    patchCatalog: builder.mutation<Catalog, PatchCatalogInput>({
      query: (body) => ({
        url: `${services.catalog}/v1/merchantCatalog`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Catalog', id },
        { type: 'CatalogList', id: 'LIST' },
        { type: 'CatalogVariant', id: `LIST:${id}` },
      ],
    }),

    /** DELETE /catalog/v1/merchantCatalog/{catalogId} */
    deleteCatalog: builder.mutation<void, { catalogId: string }>({
      query: ({ catalogId }) => ({
        url: `${services.catalog}/v1/merchantCatalog/${encodeURIComponent(catalogId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { catalogId }) => [
        { type: 'Catalog', id: catalogId },
        { type: 'CatalogList', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetCatalogsQuery,
  useLazyGetCatalogsQuery,
  useCreateCatalogMutation,
  usePatchCatalogMutation,
  useDeleteCatalogMutation,
} = catalogsApi;
